import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useCallback, useState } from 'react';
import type { CostItem, CostStatus, Owner, WorkOption } from 'used-vehicle-contracts';
import { orpc } from '../../apis/orpc-query';
import { useAuth } from '../../auth/AuthContext';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { DataTable, type DataTableColumns } from '../../components/ui/DataTable';
import { Select } from '../../components/ui/Select';
import { StatusText } from '../../components/ui/StatusText';
import { formatMoney } from '../../lib/format';
import { serverMessage, submitErrorKey } from '../../lib/form-helpers';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { CostItemSheet, type CostItemSubmission } from './CostItemSheet';
import { costStatusTone, EDITABLE_COST_STATUSES, WORK_OPTIONS } from './costs-display';

export interface CostItemsCardProps {
  owner: Owner;
  /** Open records accept edits; a settled estimate and a sold vehicle are read-only (plan §5.6). */
  editable: boolean;
  title: string;
  description?: string;
  /** Called after every write, so the page that owns the record can refresh its own figures. */
  onChanged?: () => void;
}

/** On a stock vehicle, the INVOICE documents an actual can be tied to (plan §8). Empty on an estimate. */
const useInvoiceDocuments = (owner: Owner) => {
  const query = useQuery(orpc.documents.list.queryOptions({ input: { vehicleId: owner.id }, enabled: owner.type === 'VEHICLE' }));

  return (query.data?.items ?? []).filter((document) => document.category === 'INVOICE');
};

/**
 * The cost items of one record — the estimator's "Expected work" card and the dossier's work-items table
 * are the same component with a different owner (plan §7).
 *
 * That is not a shortcut: on an estimate these rows are the *expected* work that lowers the ceiling, and
 * the purchase copies them onto the vehicle as the workshop's job list. A salesperson and a workshop
 * clerk look at the same figures in the same shape, which is what makes "estimate → actual" a comparison
 * rather than two unrelated numbers.
 */
export const CostItemsCard = ({ owner, editable, title, description, onChanged }: CostItemsCardProps) => {
  const { t } = useI18nTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [editing, setEditing] = useState<CostItem | null>(null);
  const [isSheetOpen, setSheetOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const listQuery = useQuery(orpc.costs.list.queryOptions({ input: { owner } }));
  const invoiceDocuments = useInvoiceDocuments(owner);
  const addWorkOption = useMutation(orpc.costs.addWorkOption.mutationOptions());
  const addManual = useMutation(orpc.costs.addManual.mutationOptions());
  const update = useMutation(orpc.costs.update.mutationOptions());
  const remove = useMutation(orpc.costs.remove.mutationOptions());
  const waive = useMutation(orpc.costs.waive.mutationOptions());

  const items = listQuery.data?.items ?? [];
  const summary = listQuery.data?.summary ?? { count: 0, resolvedTotal: 0, provisional: false };
  const isBusy = addWorkOption.isPending || addManual.isPending || update.isPending || remove.isPending || waive.isPending;

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: orpc.costs.list.key() });
    onChanged?.();
  }, [onChanged, queryClient]);

  /** Every write reports the server's own message; nothing here guesses why a refusal happened. */
  const run = useCallback(
    async (action: () => Promise<unknown>): Promise<boolean> => {
      setError(null);

      try {
        await action();
        await refresh();

        return true;
      } catch (caught: unknown) {
        setError(serverMessage(caught) ?? t(submitErrorKey(caught)));

        return false;
      }
    },
    [refresh, t],
  );

  const onSubmit = async (submission: CostItemSubmission) => {
    const ok = await run(async () =>
      submission.kind === 'MANUAL'
        ? addManual.mutateAsync({
            owner,
            category: submission.category,
            name: submission.name,
            estimate: submission.estimate,
            reason: submission.reason,
          })
        : update.mutateAsync({
            id: submission.id,
            estimate: submission.estimate,
            actualToDate: submission.actualToDate,
            remainingEstimate: submission.remainingEstimate,
            status: submission.status,
            supplier: submission.supplier,
            workDate: submission.workDate,
            invoiceDocumentId: submission.invoiceDocumentId,
            notes: submission.notes,
          }),
    );

    if (ok) {
      setSheetOpen(false);
      setEditing(null);
    }
  };

  const columns: DataTableColumns<CostItem> = [
    {
      id: 'name',
      accessorKey: 'name',
      header: t('costs.columnItem'),
      cell: ({ row }) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-foreground">{row.original.name}</p>
          <p className="truncate text-xs text-muted-foreground">{t(`enums.costCategory.${row.original.category}`)}</p>
        </div>
      ),
    },
    {
      id: 'estimate',
      header: t('costs.columnEstimate'),
      cell: ({ row }) => (
        <span
          dir="ltr"
          className="tabular-nums"
        >
          {formatMoney(row.original.estimate, { exact: true })}
        </span>
      ),
    },
    {
      id: 'actual',
      header: t('costs.columnActual'),
      cell: ({ row }) => (
        <span
          dir="ltr"
          className="tabular-nums"
        >
          {row.original.actualToDate === null ? t('common.notRecorded') : formatMoney(row.original.actualToDate, { exact: true })}
        </span>
      ),
    },
    {
      id: 'resolved',
      header: t('costs.columnResolved'),
      cell: ({ row }) => (
        <div className="min-w-0">
          <span
            dir="ltr"
            className="tabular-nums font-medium"
          >
            {formatMoney(row.original.resolvedAmount, { exact: true })}
          </span>
          <p className="text-xs text-muted-foreground">{t(`costs.bases.${row.original.resolvedBasis}`)}</p>
        </div>
      ),
    },
    {
      id: 'status',
      header: t('costs.columnStatus'),
      cell: ({ row }) =>
        editable && row.original.status !== 'WAIVED' ? (
          <Select
            aria-label={t('costs.status')}
            value={row.original.status}
            disabled={isBusy}
            className="h-8 w-40"
            onChange={(event) => {
              const next = event.target.value as Exclude<CostStatus, 'WAIVED'>;

              // Marking work done needs the amount it actually cost, so the row opens rather than
              // failing silently against the server's rule (plan §5.5).
              if (next === 'DONE' && row.original.actualToDate === null) {
                setEditing(row.original);
                setSheetOpen(true);

                return;
              }

              void run(async () => update.mutateAsync({ id: row.original.id, status: next }));
            }}
            options={EDITABLE_COST_STATUSES.map((status) => ({ value: status, label: t(`enums.costStatus.${status}`) }))}
          />
        ) : (
          <StatusText
            label={t(`enums.costStatus.${row.original.status}`)}
            tone={costStatusTone(row.original.status)}
          />
        ),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) =>
        editable ? (
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setEditing(row.original);
                setSheetOpen(true);
              }}
            >
              {t('actions.edit')}
            </Button>
            {row.original.status === 'ESTIMATED' ? (
              <Button
                variant="ghost"
                size="sm"
                disabled={isBusy}
                onClick={() => void run(async () => remove.mutateAsync({ id: row.original.id }))}
              >
                {t('actions.remove')}
              </Button>
            ) : null}
            {user?.role === 'ADMIN' && row.original.status !== 'WAIVED' ? (
              <Button
                variant="ghost"
                size="sm"
                disabled={isBusy}
                onClick={() => {
                  const reason = window.prompt(t('costs.waivePrompt'));

                  if (reason !== null && reason.trim() !== '') {
                    void run(async () => waive.mutateAsync({ id: row.original.id, reason: reason.trim() }));
                  }
                }}
              >
                {t('costs.waive')}
              </Button>
            ) : null}
          </div>
        ) : null,
    },
  ];

  return (
    <section className="space-y-4 rounded-lg border border-border bg-card p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-card-foreground">{title}</h2>
          {description ? <p className="mt-1 text-sm text-muted-foreground">{description}</p> : null}
        </div>
        <div className="flex items-center gap-2">
          {summary.provisional ? <Badge tone="warning">{t('costs.provisional')}</Badge> : null}
          <Badge mono>{formatMoney(summary.resolvedTotal, { exact: true })}</Badge>
        </div>
      </div>

      {error === null ? null : (
        <p
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
        >
          {error}
        </p>
      )}

      {editable ? (
        <div className="flex flex-wrap gap-2">
          {WORK_OPTIONS.map((option: Exclude<WorkOption, 'MANUAL'>) => (
            <Button
              key={option}
              variant="outline"
              size="sm"
              disabled={isBusy || items.some((item) => item.workOption === option)}
              onClick={() => void run(async () => addWorkOption.mutateAsync({ owner, workOption: option }))}
            >
              <Plus className="size-3.5" />
              {t(`enums.workOption.${option}`)}
            </Button>
          ))}
          <Button
            variant="outline"
            size="sm"
            disabled={isBusy}
            onClick={() => {
              setEditing(null);
              setSheetOpen(true);
            }}
          >
            <Plus className="size-3.5" />
            {t('costs.addManual')}
          </Button>
        </div>
      ) : null}

      <DataTable
        columns={columns}
        data={items}
        isLoading={listQuery.isLoading}
        emptyMessage={t('costs.empty')}
      />

      <p className="text-xs text-muted-foreground">{t('costs.resolutionNote')}</p>

      <CostItemSheet
        isOpen={isSheetOpen}
        item={editing}
        invoiceDocuments={invoiceDocuments}
        isSaving={isBusy}
        error={error}
        onClose={() => {
          setSheetOpen(false);
          setEditing(null);
          setError(null);
        }}
        onSubmit={(submission) => void onSubmit(submission)}
      />
    </section>
  );
};
